// src/components/MapViewComparison.jsx

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import mapboxgl from 'mapbox-gl';
import { icons } from "./icons";
import { Fab, Box, Paper, Typography } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import API_BASE_URL from '../config';

// --- Legend Component ---
const MapLegend = () => {
    const theme = useTheme();
    // This gradient is designed to visually match the 'interpolate' colors in the 'impact-lines' layer.
    const gradient = 'linear-gradient(to right, #1a9850, #91cf60, #d9ef8b, #fafafa, #fee08b, #fc8d59, #d73027)';

    return (
        <Paper
            sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                padding: '12px',
                borderRadius: '8px',
                zIndex: 1,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                minWidth: '200px',
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
                Flow Change
            </Typography>
            <Box
                sx={{
                    height: '12px',
                    background: gradient,
                    borderRadius: '4px',
                    border: `1px solid ${theme.palette.divider}`,
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 0.5,
                }}
            >
                <Typography variant="caption">Decrease</Typography>
                <Typography variant="caption">Increase</Typography>
            </Box>
        </Paper>
    );
};


const MapViewComparison = ({ system = 'water', selectedFeature = null, baseScenarioId, comparisonScenarioId }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const popup = useRef(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isStyleReady, setIsStyleReady] = useState(false);
    const setupInProgress = useRef(false);
    const theme = useTheme();

    const getTileUrl = useCallback((sys) => {
        const url = `${API_BASE_URL}/api/tiles/comparison/${baseScenarioId}/${comparisonScenarioId}/${sys}/{z}/{x}/{y}.pbf`;
        return url;
    }, [baseScenarioId, comparisonScenarioId]);

    // --- THIS IS THE FINAL FIX ---
    const getSourceLayerName = useCallback((sys) => {
        // This must match the internal layer name generated by the ComparisonTileView
        return `comparison_analysis_output_${sys}`;
    }, []);
    // --- END OF FINAL FIX ---

    const getSeverityColor = useCallback((severity) => {
        switch (severity?.toLowerCase()) {
            case 'extreme': return '#FFFF00'; case 'severe': return '#FFA500';
            case 'high': return '#FF0000'; case 'medium': return '#8B0000';
            case 'low': return '#800080'; default: return theme.palette.mode === 'dark' ? '#555' : '#D3D3D3';
        }
    }, [theme.palette.mode]);

    const addIconToMap = useCallback((url, id, color = null) => {
        return new Promise((resolve, reject) => {
            if (!map.current) return reject(new Error(`Map not initialized for icon: ${id}`));
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = url;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = img.width || 24;
                canvas.height = img.height || 24;
                context.drawImage(img, 0, 0, canvas.width, canvas.height);
                if (color) {
                    context.globalCompositeOperation = 'source-atop';
                    context.fillStyle = color;
                    context.fillRect(0, 0, canvas.width, canvas.height);
                }
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                try {
                    if (map.current.hasImage(id)) map.current.removeImage(id);
                    map.current.addImage(id, imageData);
                    resolve();
                } catch (error) {
                    reject(new Error(`Failed to add image ${id} to map: ${error.message}`));
                }
            };
            img.onerror = (error) => reject(new Error(`Failed to load image for icon ${id}: ${error}`));
        });
    }, []);

    const classifyValue = useCallback((value) => {
        if (value === undefined || value === null || value <= 0) return 'none';
        if (value <= 1e-6) return 'none'; if (value <= 100) return 'low';
        if (value <= 500) return 'medium'; if (value <= 2000) return 'high';
        if (value <= 10000) return 'severe'; return 'extreme';
    }, []);

    const createImpactPopupContent = useCallback((properties) => {
        const code = properties.code || properties.node_id || properties.edge_id;
        const name = properties.name; const type = properties.type;
        const flowDiffProp = `flow_diff_${system}`; const flowDiffValue = properties[flowDiffProp];
        const flowBaseProp = `flow_base_${system}`; const flowBaseValue = properties[flowBaseProp];
        const flowCompProp = `flow_comp_${system}`; const flowCompValue = properties[flowCompProp];
        const isTransport = !['power', 'water'].includes(system);
        const metricProp = isTransport ? `restricted_${system}` : `unmet_${system}`;
        const metricValue = properties[metricProp];
        const metricLabel = isTransport ? `Restricted Supply` : `Unmet Demand`;
        const severity = properties.severity || classifyValue(metricValue);
        let content = `<div class="popup-container"><div class="popup-header"><strong style="color: #000;">${code || name || type || 'Unknown Feature'}</strong></div><div class="popup-body">`;
        if (name && name !== code) content += `<p><strong>Name:</strong> ${name}</p>`;
        if (type) content += `<p><strong>Type:</strong> ${type}</p>`;
        if (flowDiffValue !== undefined) {
            const sign = flowDiffValue > 0 ? '+' : '';
            const color = flowDiffValue > 0 ? 'green' : (flowDiffValue < 0 ? 'red' : 'grey');
            content += `<p><strong>Flow Change:</strong> <span style="color: ${color}; font-weight: bold;">${sign}${parseInt(flowDiffValue, 10)?.toLocaleString() ?? 'N/A'}</span></p>`;
            if (flowBaseValue !== undefined) content += `<p><strong>Base Flow:</strong> ${parseInt(flowBaseValue, 10)?.toLocaleString() ?? 'N/A'}</p>`;
            if (flowCompValue !== undefined) content += `<p><strong>Comparison Flow:</strong> ${parseInt(flowCompValue, 10)?.toLocaleString() ?? 'N/A'}</p>`;
        }
        if (metricValue !== undefined && metricValue > 0) {
            content += `<p><strong>${metricLabel}:</strong> ${parseInt(metricValue, 10)?.toLocaleString() ?? 'N/A'}</p>`;
            content += `<p><strong>Criticality:</strong> ${severity !== 'none' ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'None'}</p>`;
        }
        content += `</div></div>`;
        return content;
    }, [system, classifyValue]);

    const addPopupEventListeners = useCallback(() => {
        if (!map.current) return;
        if (!popup.current) { popup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'custom-popup' }); }
        const handleMouseEnter = (e) => {
            if (!map.current || !e.features || e.features.length === 0) return;
            const feature = e.features[0];
            if (feature.properties?.type === 'Road') return;
            map.current.getCanvas().style.cursor = 'pointer';
            popup.current.setLngLat(e.lngLat).setHTML(createImpactPopupContent(feature.properties)).addTo(map.current);
        };
        const handleMouseLeave = () => {
            if (!map.current) return;
            map.current.getCanvas().style.cursor = '';
            if (popup.current) popup.current.remove();
        };
        const layers = ['impact-nodes', 'impact-lines'];
        layers.forEach(layerId => {
            if (map.current.getLayer(layerId)) {
                map.current.off('mouseenter', layerId, handleMouseEnter);
                map.current.off('mouseleave', layerId, handleMouseLeave);
                map.current.on('mouseenter', layerId, handleMouseEnter);
                map.current.on('mouseleave', layerId, handleMouseLeave);
            }
        });
    }, [createImpactPopupContent]);

    const addLayers = useCallback(() => {
        if (!map.current) return;

        try {
            const layersToRemove = ['impact-nodes', 'context-nodes', 'impact-lines', 'base-lines'];
            layersToRemove.forEach(id => {
                if (map.current.getLayer(id)) map.current.removeLayer(id);
            });
            if (map.current.getSource('network-tiles')) map.current.removeSource('network-tiles');

            const tileUrl = getTileUrl(system);
            const sourceLayerName = getSourceLayerName(system);
            
            map.current.addSource('network-tiles', {
                type: 'vector', tiles: [tileUrl], minzoom: 0, maxzoom: 12
            });

            const flowDiffProp = `flow_diff_${system}`;
            const isTransport = !['power', 'water'].includes(system);
            const impactNodeProp = isTransport ? `restricted_${system}` : `unmet_${system}`;
            const impactNodeSevProp = isTransport ? `restricted_sev_${system}` : `unmet_sev_${system}`;
            const getIconName = (baseName) => theme.palette.mode === 'dark' ? `${baseName}-dark` : baseName;

            map.current.addLayer({
                id: 'base-lines', type: 'line', source: 'network-tiles', 'source-layer': sourceLayerName,
                filter: ['==', '$type', 'LineString'],
                paint: { 'line-color': theme.palette.mode === 'dark' ? '#444' : '#E0E0E0', 'line-width': 0.8 }
            });

            map.current.addLayer({
                id: 'impact-lines', type: 'line', source: 'network-tiles', 'source-layer': sourceLayerName,
                filter: ['all', ['==', '$type', 'LineString'], ['has', flowDiffProp]],
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                    'line-color': [
                        'interpolate', ['linear'], ['get', flowDiffProp],
                        -10000, '#1a9850',
                        -500,   '#91cf60',
                        -1,     '#d9ef8b',
                        0,     'transparent',
                        1,     '#fee08b',
                        500,   '#fc8d59',
                        10000, '#d73027'
                    ],
                    'line-width': 2.5,
                    'line-opacity': ['case', ['!=', ['get', flowDiffProp], 0], 1.0, 0.0]
                }
            });

            map.current.addLayer({
                id: 'context-nodes', type: 'symbol', source: 'network-tiles', 'source-layer': sourceLayerName,
                filter: ['all', 
                    ['==', '$type', 'Point'], 
                    ['!=', 'name', ''], 
                    ['!=', 'type', ''], 
                    ['any', 
                        ['!has', impactNodeProp], 
                        ['<=', ['get', impactNodeProp], 0]
                    ]
                ],
                layout: { 
                    'icon-image': [
                        'match', ['get', 'name'],
                        'School', getIconName('school'), 'Port', getIconName('directions-boat'), 'Harbour', getIconName('directions-boat'),
                        'Household', getIconName('home'), 'Supermarket', getIconName('local-grocery-store'), 'Warehouse', getIconName('warehouse'),
                        'Farm', getIconName('agriculture'), 'Power station', getIconName('factory'), 'Power substation', getIconName('power'),
                        'Hospital', getIconName('local-hospital'), 'Clinic', getIconName('local-hospital'), 'Pharmacy', getIconName('pharmacy'),
                        'Fire station', getIconName('fire-station'), 'Police station', getIconName('police'), 'Military base', getIconName('police'),
                        'Train station', getIconName('train'), 'Airport', getIconName('plane'), 'Government office', getIconName('office'),
                        'Courthouse', getIconName('office'), 'City hall', getIconName('office'), 'Communication tower', getIconName('communication-tower'),
                        'Water reservoir', getIconName('reservoir'), 'Water plant', getIconName('droplet'), 'Wastewater plant', getIconName('droplet'),
                        getIconName('home')
                    ],
                    'icon-size': 0.8, 'icon-allow-overlap': true 
                },
                paint: { 'icon-opacity': 0.35 }, minzoom: 12
            });

            const iconMatchList = [
                "Road", "road", 'School', 'school', 'Port', 'directions-boat', 'Harbour', 'directions-boat',
                'Household', 'home', 'Supermarket', 'local-grocery-store', 'Warehouse', 'warehouse',
                'Farm', 'agriculture', 'Power station', 'factory', 'Power substation', 'power',
                'Hospital', 'local-hospital', 'Clinic', 'local-hospital', 'Pharmacy', 'pharmacy',
                'Fire station', 'fire-station', 'Police station', 'police', 'Military base', 'police',
                'Train station', 'train', 'Airport', 'plane', 'Government office', 'office',
                'Courthouse', 'office', 'City hall', 'office', 'Communication tower', 'communication-tower',
                'Water reservoir', 'reservoir', 'Water plant', 'droplet', 'Wastewater plant', 'droplet',
                'dummy-transparent'
            ];

            map.current.addLayer({
                id: 'impact-nodes', type: 'symbol', source: 'network-tiles', 'source-layer': sourceLayerName,
                filter: ['all', 
                    ['==', '$type', 'Point'], 
                    ['!=', 'name', ''], 
                    ['!=', 'type', ''], 
                    ['has', impactNodeProp], 
                    ['>', ['get', impactNodeProp], 0]
                ],
                layout: {
                    'icon-image': ['concat', ['match', ['get', 'name'], ...iconMatchList], '-', ['get', impactNodeSevProp]],
                    'icon-size': 1.1, 'icon-allow-overlap': true,
                }, minzoom: 12
            });

            addPopupEventListeners();
        
        } catch (error) {
            console.error("[MapViewComparison] Error adding layers:", error);
        }
    }, [system, theme.palette.mode, getTileUrl, getSourceLayerName, addPopupEventListeners]);

    const setupIconsAndLayers = useCallback(async () => {
        if (!map.current || setupInProgress.current) return;
        setupInProgress.current = true;
        try {
            await new Promise(resolve => {
                if (map.current.isStyleLoaded()) return resolve();
                map.current.once('style.load', resolve);
            });
            const iconPromises = [];
            if (typeof icons === 'object' && icons !== null) {
                for (const [name, url] of Object.entries(icons)) {
                    if (name !== 'dummy-transparent') iconPromises.push(addIconToMap(url, name));
                }
            }
            const severityLevels = ['extreme', 'severe', 'high', 'medium', 'low'];
            if (typeof icons === 'object' && icons !== null) {
                for (const [iconKey, sourceUrl] of Object.entries(icons)) {
                    const baseName = iconKey.replace('-dark', '');
                    if (iconKey.endsWith('-dark') || iconKey === 'dummy-transparent' || !sourceUrl) continue;
                    for (const severity of severityLevels) {
                        const color = getSeverityColor(severity);
                        const newIconId = `${baseName}-${severity}`;
                        iconPromises.push(addIconToMap(sourceUrl, newIconId, color));
                    }
                }
            }
            await Promise.allSettled(iconPromises);
            addLayers();
        } catch (error) {
            console.error("[MapViewComparison] Critical failure during setup:", error);
        } finally {
            setupInProgress.current = false;
        }
    }, [addIconToMap, getSeverityColor, addLayers]);

    useEffect(() => {
        mapboxgl.accessToken = 'pk.eyJ1IjoibXZwYXNzb3MiLCJhIjoiY2t6em4xcnpwMGJ2bDNicDYyemM0bWU4YyJ9.fR55BgEafulrz4yLfY2oRA';
        if (map.current) return;
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
            center: [18.0656, 59.3327], zoom: 12, preserveDrawingBuffer: true
        });
        const handleMapLoad = () => setIsMapLoaded(true);
        const handleStyleLoad = () => setIsStyleReady(true);
        map.current.on('load', handleMapLoad);
        map.current.on('style.load', handleStyleLoad);
        const mapRef = map.current;
        return () => {
            if (mapRef) {
                mapRef.off('load', handleMapLoad);
                mapRef.off('style.load', handleStyleLoad);
                mapRef.remove();
            }
            map.current = null;
            setupInProgress.current = false;
        };
    }, [theme.palette.mode]);

    useEffect(() => {
        if (isMapLoaded && isStyleReady && map.current && !setupInProgress.current) {
            setupIconsAndLayers();
        }
    }, [isMapLoaded, isStyleReady, setupIconsAndLayers, system, baseScenarioId, comparisonScenarioId]);

    useEffect(() => {
        if (isMapLoaded && isStyleReady && map.current && map.current.isStyleLoaded() && selectedFeature && selectedFeature.coords) {
            const { coords, properties } = selectedFeature;
            map.current.flyTo({ center: coords, zoom: 16, essential: true });
            if (!popup.current) { popup.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'custom-popup' }); }
            popup.current.setLngLat(coords).setHTML(createImpactPopupContent(properties)).addTo(map.current);
        } else if (popup.current && !selectedFeature) {
            popup.current.remove();
        }
    }, [selectedFeature, isMapLoaded, isStyleReady, createImpactPopupContent]);

    const exportMapAsPng = useCallback(() => {
        if (!map.current) return;
        try {
            const canvas = map.current.getCanvas();
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `map-comparison-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting map as PNG:', error);
            alert('Failed to export map. Please try again.');
        }
    }, []);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 'inherit' }}>
            <div ref={mapContainer} style={{ width: '100%', height: '100%', borderRadius: 'inherit' }} />
            
            {/* Legend */}
            <MapLegend />

            {/* Export Button */}
            <Box position="absolute" bottom={16} right={16} zIndex={1}>
                <Fab color="primary" aria-label="export" onClick={exportMapAsPng} size="small">
                    <DownloadIcon />
                </Fab>
            </Box>
        </div>
    );
};

export default MapViewComparison;